import React, { Component } from 'react';
import classNames from 'classnames';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { formatCurrencyMajorUnit } from '../../../util/currency';

import css from './PriceFilterPopup.module.css';
import { OutsideClickHandler } from '../../../components';
import PriceFilterForm from '../PriceFilterForm/PriceFilterForm';
import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';

const RADIX = 10;
const KEY_CODE_ESCAPE = 27;
const hasValue = value => value != null;

const getPriceQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'price';
};

// Parse value, which should look like "0,1000"
const parse = priceRange => {
  const [minPrice, maxPrice] = !!priceRange
    ? priceRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];
  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  return !!priceRange && minPrice != null && maxPrice != null ? { minPrice, maxPrice } : null;
};

// Format value, which should look like { minPrice, maxPrice }
const format = (range, queryParamName) => {
  const { minPrice, maxPrice } = range || {};

  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const value = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;

  return { [queryParamName]: value };
};

// Format value for bounds, which should look like { bounds }
const formatBounds = (values, queryParamName) => {
  const { bounds, address, minPrice, maxPrice } = values || {};

  // Note: we compare to null, because 0 as minPrice is falsy in comparisons.
  const radius = minPrice != null ? `${minPrice}` : null;
  const priceRange = minPrice != null && maxPrice != null ? `${minPrice},${maxPrice}` : null;

  return { address, bounds, radius };
  // return { address, bounds, radius, [queryParamName]: priceRange };
};

class PriceFilterPopup extends Component {
  constructor(props) {
    super(props);

    this.filter = null;
    this.filterContent = null;
    this.state = { isOpen: false };

    this.handleBlur = this.handleBlur.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  // handleSubmit(values) {
  //   const { onSubmit, queryParamNames } = this.props;
  //   this.setState({ isOpen: false });
  //   const priceQueryParamName = getPriceQueryParamName(queryParamNames);
  //   onSubmit(format(values, priceQueryParamName));
  // }

  handleSubmit(values) {
    console.log(values);
    const { onSubmit, queryParamNames } = this.props;
    this.setState({ isOpen: false });
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(formatBounds(values, priceQueryParamName));
  }

  handleClear() {
    const { onSubmit, queryParamNames } = this.props;
    this.setState({ isOpen: false });
    const priceQueryParamName = getPriceQueryParamName(queryParamNames);
    onSubmit(format(null, priceQueryParamName));
  }

  handleCancel() {
    const { onSubmit, initialValues } = this.props;
    this.setState({ isOpen: false });
    onSubmit(initialValues);
  }

  handleBlur() {
    this.setState({ isOpen: false });
  }

  handleKeyDown(e) {
    // Gather all escape presses to close menu
    if (e.keyCode === KEY_CODE_ESCAPE) {
      this.toggleOpen(false);
    }
  }

  toggleOpen(enforcedState) {
    if (enforcedState) {
      this.setState({ isOpen: enforcedState });
    } else {
      this.setState(prevState => ({ isOpen: !prevState.isOpen }));
    }
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset ?? 0;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };

      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }

    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      label,
      queryParamNames,
      initialValues,
      min,
      max,
      step = 1,
      intl,
      location,
      marketplaceCurrency,
    } = this.props;
    const priceQueryParam = getPriceQueryParamName(queryParamNames);
    const classes = classNames(rootClassName || css.root, className);
    const radius = new URLSearchParams(location.search).get('radius');
    const initialPrice =
      initialValues && initialValues[priceQueryParam] ? parse(initialValues[priceQueryParam]) : {};

    const { minPrice, maxPrice } = initialPrice || {};
    const contentStyle = this.positionStyleForContent();
    const hasInitialValues = initialValues && hasValue(minPrice) && hasValue(maxPrice);

    const hasRadiusValue = hasValue(radius);
    const labelStyles = hasRadiusValue ? css.labelSelected : css.label;
    const currentLabel = hasRadiusValue
      ? intl.formatMessage({ id: 'RadiusFilter.labelSelectedButton' }, { radius })
      : label
      ? label
      : intl.formatMessage({ id: 'RadiusFilter.label' });

    // const currentLabel = hasInitialValues
    //   ? intl.formatMessage(
    //       { id: 'PriceFilter.labelSelectedButton' },
    //       {
    //         minPrice: formatCurrencyMajorUnit(intl, marketplaceCurrency, minPrice),
    //         maxPrice: formatCurrencyMajorUnit(intl, marketplaceCurrency, maxPrice),
    //       }
    //     )
    //   : label
    //   ? label
    //   : intl.formatMessage({ id: 'PriceFilter.label' });

    return (
      <OutsideClickHandler onOutsideClick={this.handleBlur}>
        <div
          className={classes}
          onKeyDown={this.handleKeyDown}
          ref={node => {
            this.filter = node;
          }}
        >
          <PopupOpenerButton isSelected={hasRadiusValue} toggleOpen={this.toggleOpen}>
            {currentLabel}
          </PopupOpenerButton>

          <PriceFilterForm
            id={id}
            min={min}
            max={max}
            step={step}
            intl={intl}
            showAsPopup
            style={contentStyle}
            isOpen={this.state.isOpen}
            onClear={this.handleClear}
            onCancel={this.handleCancel}
            onSubmit={this.handleSubmit}
            contentRef={node => {
              this.filterContent = node;
            }}
            initialValues={hasInitialValues ? initialPrice : { minPrice: min, maxPrice: max }}
          />
        </div>
      </OutsideClickHandler>
    );
  }
}

export default compose(
  withRouter,
  injectIntl
)(PriceFilterPopup);
